import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Upload } from 'lucide-react';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { formatFileSize } from '@/utils/number';
import {
  PCMP_MKT_COMMENT_MAX,
  PCMP_MKT_PROOF_ACCEPT,
  PCMP_MKT_PROOF_MAX_BYTES,
} from '../../constants/pcmpProposal.constants';
import { useMktDisbursePcmpProposal } from '../../hooks/useMktDisbursePcmpProposal';
import { resolvePcmpUploadMimeType as resolveProofMimeType } from '../../utils/pcmpUploadFile';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpCommentField } from './PcmpCommentField';

const disburseFormSchema = z.object({
  proof: z
    .custom<File | null>((value) => value === null || value instanceof File)
    .refine((file) => file !== null, 'Payment proof is required.')
    .refine(
      (file) => file === null || resolveProofMimeType(file) !== null,
      'Proof must be a JPEG, PNG or PDF file.',
    )
    .refine(
      (file) => file === null || file.size <= PCMP_MKT_PROOF_MAX_BYTES,
      `File is too large. Maximum allowed size is ${formatFileSize(PCMP_MKT_PROOF_MAX_BYTES)}.`,
    ),
  comment: z.string().trim().min(1, 'A comment is required.').max(PCMP_MKT_COMMENT_MAX),
});

type DisburseFormInput = z.input<typeof disburseFormSchema>;
type DisburseFormValues = z.output<typeof disburseFormSchema>;

export interface PcmpMktDisburseTarget {
  proposalId: number;
  refNo: string;
}

interface PcmpMktDisburseDialogProps {
  target: PcmpMktDisburseTarget;
  onClose: () => void;
}

/**
 * Legacy MktDisburse: on the Marketing channel the uploaded payment proof is
 * the payout, so the proposal is disbursed at once (no MIS approval).
 */
export function PcmpMktDisburseDialog({ target, onClose }: PcmpMktDisburseDialogProps) {
  const form = useForm<DisburseFormInput, unknown, DisburseFormValues>({
    resolver: zodResolver(disburseFormSchema),
    defaultValues: { proof: null, comment: '' },
  });
  const { mutate, isPending, error } = useMktDisbursePcmpProposal(target.proposalId, onClose);

  const onSubmit = form.handleSubmit(({ proof, comment }) => {
    const mimeType = proof ? resolveProofMimeType(proof) : null;
    // The schema already rejected both; this narrows the types.
    if (!proof || !mimeType) return;
    mutate({ proof, mimeType, comment });
  });

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Marketing Disbursement"
        description={target.refNo}
        icon={Upload}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Disburse"
        pendingLabel="Uploading…"
      >
        <p className="text-sm text-muted-foreground">
          Upload the payment proof. This completes the marketing disbursement; there is no further
          approval.
        </p>
        <FormField
          control={form.control}
          name="proof"
          render={({ field: { value, onChange, onBlur, name, ref } }) => (
            <FormItem>
              <FormLabel>
                Payment proof<span className="text-destructive"> *</span>
              </FormLabel>
              <FormControl>
                <Input
                  ref={ref}
                  name={name}
                  type="file"
                  accept={PCMP_MKT_PROOF_ACCEPT}
                  onBlur={onBlur}
                  onChange={(event) => onChange(event.target.files?.[0] ?? null)}
                  className="cursor-pointer"
                />
              </FormControl>
              <FormDescription>
                {value
                  ? `${value.name} · ${formatFileSize(value.size)}`
                  : `JPG, PNG or PDF, up to ${formatFileSize(PCMP_MKT_PROOF_MAX_BYTES)}.`}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required
          placeholder="Add a comment..."
        />
      </PcmpEditDialog>
    </Form>
  );
}
